import {
  ComponentType,
  CSSProperties,
  FC,
  memo,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import ResizeObserver from "resize-observer-polyfill";
import { SIDE } from "_constants";
import { getTeamColors } from "../colors";
import { Text } from "./Text";

type BaseProps = {
  className?: string;
  team?: string | null;
  side: SIDE;
  Icon?: ComponentType<any> | null;
  title?: string;
  description?: string;
  normalizedCode: string; // nsc
  status: any; // match status (x,y,nsc vs)
  width: number;
  height: number;
  bottomCodes?: string[];
  hideCode?: string;
  sideIconResolver?: (
    normalizedCode: string,
    side: "home" | "away"
  ) => ComponentType<any> | null;
  accentColor?: string;
  forceNoTeamLayout?: boolean;
};

export const StatusMessageBase: FC<BaseProps> = memo(
  ({
    team,
    side: _side,
    Icon,
    title,
    description,
    className,
    normalizedCode,
    status: matchStatus,
    width: overlayWidth,
    height: overlayHeight,
    bottomCodes = [],
    hideCode,
    sideIconResolver,
    accentColor,
    forceNoTeamLayout = false,
  }) => {
    const accent = accentColor ?? "#043e00";
    const { x: _x = 0.5, y: _y = 0.5, nsc } = matchStatus || {};
    const { home: homeColor, away: awayColor } = getTeamColors(matchStatus);
    let x = _x || 0.5;
    let y = _y || 0.5;

    if (x === 0 && y === 0) {
      x = 0.5;
      y = 0.5;
    }

    const side = _side === SIDE.AWAY ? "away" : "home";
    const [{ messageHeight, messageWidth }, setState] = useState({
      messageWidth: 0,
      messageHeight: 0,
    });
    const messageContainerRef = useRef<HTMLDivElement>(null);

    const teamName = team ?? undefined;

    useLayoutEffect(() => {
      const ro = new ResizeObserver((entries: any) => {
        for (const { contentRect } of entries) {
          const { width, height } = contentRect;
          setState({ messageWidth: width, messageHeight: height });
        }
      });
      if (messageContainerRef.current) ro.observe(messageContainerRef.current);
      return () => ro.disconnect();
    }, []);

    const placeMessage = useCallback(
      (_x: number, _y: number) => {
        let x = _x - messageWidth / 2;
        let y = _y - messageHeight / 2;

        const xLeft = _x - messageWidth - overlayWidth / 15;
        const xRight = _x + overlayWidth / 15;

        x = side === "home" ? xLeft : xRight;

        if (x < 0) x = 10;
        if (x + messageWidth > overlayWidth)
          x = overlayWidth - messageWidth - 10;

        y += (y < overlayHeight / 2 ? 1 : -1) * messageHeight * 1.5;

        x = Math.max(10, Math.min(x, overlayWidth - messageWidth - 10));
        y = Math.max(10, Math.min(y, overlayHeight - messageHeight - 10));

        return [x, y];
      },
      [side, messageWidth, messageHeight, overlayWidth, overlayHeight]
    );

    const isBottomBanner = bottomCodes.includes(String(normalizedCode));

    const IconToRender = useMemo(() => {
      if (sideIconResolver) {
        const comp = sideIconResolver(String(normalizedCode), side);
        if (comp) return comp;
      }
      return Icon || null;
    }, [side, normalizedCode, Icon, sideIconResolver]);

    if (hideCode && nsc === hideCode) return <></>;

    // BOTTOM DATA (ALWAYS PRIORITY)
    if (isBottomBanner)
      return (
        <div
          className={[
            "status-message-container absolute top-[87%] z-[999] flex",
            `nsc-${normalizedCode}`,
            className,
          ]
            .filter(Boolean)
            .join(" ")}
          data-side={side}
          style={{
            transform: "translate(var(--x))",
            transition: "transform 300ms ease",
            left: side === "home" ? "10%" : undefined,
            right: side === "away" ? "10%" : undefined,
          }}
        >
          <div
            className="inner-container my-auto flex min-h-[5vw] flex-row items-center gap-[1vw] px-[0.5vw]"
            ref={messageContainerRef}
            style={{
              ...buildBorderStyles({
                side,
                homeColor,
                awayColor,
              }),
            }}
          >
            <div
              className={[
                "flex flex-col",
                side === "home"
                  ? "items-start text-left"
                  : "items-end text-right",
              ]
                .filter(Boolean)
                .join(" ")}
            >
              {teamName && <Text variant="statusMessageTitle">{teamName}</Text>}
              <Text variant="statusMessageTitle" className="font-black">
                {title}
              </Text>

              {description && (
                <Text
                  variant="statusMessageDescription"
                  className="uppercase font-black"
                >
                  {description}
                </Text>
              )}
            </div>
          </div>
        </div>
      );

    // NO TEAM NAME MAIN DATA (only when not bottom)
    if (forceNoTeamLayout || !teamName)
      return (
        <div className="flex h-full w-full items-center justify-center">
          <div
            className="flex w-[50vw] items-center justify-center text-center"
            style={{
              background: `linear-gradient(180deg, ${accent} 0%, rgba(16, 53, 72, 0) 100%)`,
              clipPath: "polygon(15% -40%, 85% -40%, 100% 100%, 0% 100%)",
            }}
          >
            <div className="flex w-full flex-col items-center justify-center text-white">
              {IconToRender && (
                <div className="icon-container -mt-[5vw] flex justify-center px-[1vw]">
                  <IconToRender style={{ height: "14vw", width: "auto" }} />
                </div>
              )}
              <div className="my-[2vw] w-full bg-black/50 p-[1.5vw] font-extrabold">
                <Text variant="statusMessageBigTitle">{title}</Text>
              </div>
            </div>
          </div>
        </div>
      );

    // CENTERED DATA WITH TEAM NAME
    const ballPosition = {
      x: (x || 0.48) * overlayWidth,
      y: (y || 0.48) * overlayHeight,
    };

    let [messageX, messageY] = placeMessage(ballPosition.x, ballPosition.y);

    if (messageX === 0 && messageY === 0) {
      messageY = overlayHeight / 2;
      messageX = overlayWidth / 2;
    }

    return (
      <div
        className={[
          "status-message-container absolute z-[999] top-[6%] flex h-full w-full items-center justify-center",
          `nsc-${normalizedCode}`,
          className,
        ]
          .filter(Boolean)
          .join(" ")}
        data-side={side}
        style={{
          transform: "translate(var(--x))",
          transition: "transform 300ms ease",
          bottom: "15%",
        }}
      >
        <div
          className="inner-container my-auto flex min-h-[5vw] min-w-[30vw] flex-row items-center gap-[1vw] p-[0.5vw] text-white"
          ref={messageContainerRef}
          style={{
            ...buildBorderStyles({
              side,
              homeColor,
              awayColor,
            }),
            background:
              side === "home"
                ? `linear-gradient(270deg, rgba(0, 0, 0, 0) -3.46%, ${accent} 100.2%)`
                : `linear-gradient(90deg, rgba(0, 0, 0, 0) -3.46%, ${accent} 100.2%)`,
            justifyContent: side === "home" ? "flex-start" : "flex-end",
          }}
        >
          {side === "home" && IconToRender && (
            <div className="icon-container px-[1vw]">
              <IconToRender style={{ height: "5vw", width: "5vw" }} />
            </div>
          )}
          <div
            className={[
              "flex flex-col",
              side === "home"
                ? "items-start text-left"
                : "items-end text-right",
            ]
              .filter(Boolean)
              .join(" ")}
          >
            <Text variant="statusMessageTitle">{teamName}</Text>
            <Text variant="statusMessageTitle" className="font-black">
              {title}
            </Text>
            {description && (
              <Text
                variant="statusMessageTitle"
                className="uppercase font-black"
              >
                {description}
              </Text>
            )}
          </div>
          {side === "away" && IconToRender && (
            <div className="icon-container px-[1vw]">
              <IconToRender style={{ height: "5vw", width: "5vw" }} />
            </div>
          )}
        </div>
      </div>
    );
  }
);

const buildBorderStyles = ({
  side,
  homeColor,
  awayColor,
}: {
  side: "home" | "away";
  homeColor?: string;
  awayColor?: string;
}): CSSProperties => {
  const styles: CSSProperties = {};
  if (side === "home") {
    styles.borderLeft = `0.5vw solid ${homeColor}`;
    styles.paddingLeft = "1vw";
  } else {
    styles.borderRight = `0.5vw solid ${awayColor}`;
    styles.paddingRight = "1vw";
  }
  return styles;
};
